import React, { useEffect, memo, useState, useCallback, useRef } from 'react';
import { Card, Button, theme } from 'antd';
import { CloseOutlined, PushpinOutlined } from '@ant-design/icons';
import { useStore, useSelectedNodeId, usePipelineActions } from '../../store/store';
import { useLayoutStore, type DockPosition } from '../../store/layoutStore';
import { PropertyWizard } from './PropertyWizard';
import { PropertyValueProvider } from './PropertyValueContext';
import type { PropertyUIConfig, PropertyValueManagerConfig } from './types';
import { transformsService, workflowsService, telemetryService } from '../../api';
import type { ActionTransformUI } from '../../api/types';
import { dropdownCache } from './utils/dropdownCache';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('PropertiesPanel');


const PropertiesPanel: React.FC = memo(() => {
  const { token } = theme.useToken();
  const [uiConfig, setUIConfig] = useState<PropertyUIConfig | null>(null);
  const [valueManagerConfig, setValueManagerConfig] = useState<PropertyValueManagerConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentValues, setCurrentValues] = useState<Record<string, any>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, any>>({});
  const [draftValues, setDraftValues] = useState<Record<string, any>>({});
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hoveredDockZone, setHoveredDockZone] = useState<DockPosition | null>(null);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  
  // Ref to prevent duplicate loading calls
  const isLoadingRef = useRef(false);

  // Layout store for docking
  const { 
    propertiesPanelDock, 
    propertiesPanelSize, 
    setPropertiesPanelDock, 
    setPropertiesPanelSize 
  } = useLayoutStore();

  // Get stable selected node ID and double-click state
  const { nodeId, isDoubleClicked } = useSelectedNodeId();
  
  // Get selected node and actions from store
  const { 
    selectedNode, 
    updateNodeData, 
    setSelectedNodeId, 
    activePipeline,
    initializeTransformState,
    updateTransformState,
    cacheTransformValues,
    markTransformAsModified,
    getTransformState,
    invalidatePipelineTransformCache
  } = useStore((state) => {
    const activePipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
    const selectedNode = activePipeline?.nodes.find((n) => n.id === nodeId);
    return {
      selectedNode,
      updateNodeData: state.updateNodeData,
      setSelectedNodeId: state.setSelectedNodeId,
      activePipeline,
      initializeTransformState: state.initializeTransformState,
      updateTransformState: state.updateTransformState,
      cacheTransformValues: state.cacheTransformValues,
      markTransformAsModified: state.markTransformAsModified,
      getTransformState: state.getTransformState,
      invalidatePipelineTransformCache: state.invalidatePipelineTransformCache,
    };
  });

  // Get pipeline actions for structural changes
  const { markStructuralChange } = usePipelineActions();

  // Helper function to determine if current context is workflow vs pipeline
  const isWorkflowAction = useCallback(() => {
    // Check if the active pipeline is actually a workflow
    return activePipeline?.type === 'workflow';
  }, [activePipeline?.type]);

  // Helper function to normalize UI configuration response
  const normalizeUIConfig = useCallback((config: ActionTransformUI): PropertyUIConfig => {
    // Handle both new generalized format and legacy formats
    const name = config.name || (config as any).actionName || (config as any).transformName;
    const type = config.type || (config as any).actionType || (config as any).transformType;
    return {
      transformName: name,
      transformType: type,
      description: config.description || '',
      pages: config.pages.map(page => ({
        id: page.id,
        title: page.title,
        description: page.description,
        components: page.components.map(component => ({
          id: component.id,
          type: component.type,
          label: component.label,
          description: component.description,
          required: component.required,
          disabled: component.disabled,
          defaultValue: component.defaultValue,
          validation: component.validation,
          additionalOptions: component.additionalOptions
        }))
      }))
    };
  }, []);


  // Load configuration when selected node changes
  useEffect(() => {
    logger.debug('🎯 PropertiesPanel useEffect triggered:', {
      nodeId,
      hasSelectedNode: !!selectedNode,
      isDoubleClicked,
      nodeType: selectedNode?.type,
      nodeDataType: selectedNode?.data?.type,
      isAlreadyLoading: isLoadingRef.current,
      loading: loading
    });

    // Prevent duplicate loading calls
    if (isLoadingRef.current) {
              logger.debug('⏸️ Skipping load - already in progress');
      return;
    }

    // Don't close the panel if we're currently loading (this prevents closing during async operations)
    if (loading && !nodeId) {
              logger.debug('⏸️ Skipping close - currently loading');
      return;
    }

    if (nodeId && selectedNode && isDoubleClicked) {
      // Use the transform type from node data, not the React Flow node type
      const transformType = selectedNode.data?.type || selectedNode.type;
      logger.debug('🔍 Loading configuration for transform type:', transformType, 'from selectedNode.data.type:', selectedNode.data?.type, 'selectedNode.type:', selectedNode.type);
      loadTransformConfiguration(transformType, selectedNode.data);
    } else if (!loading) {
      // Only close the panel if we're not currently loading
      logger.debug('❌ Not loading configuration because:', {
        nodeId,
        hasSelectedNode: !!selectedNode,
        isDoubleClicked,
        loading: loading,
        reason: !nodeId ? 'no node selected' : !selectedNode ? 'selected node not found' : !isDoubleClicked ? 'node not double-clicked' : 'unknown'
      });
      
      // Clear all caches when closing the properties panel
      if (activePipeline?.id) {
        logger.debug('🧹 Clearing all caches on panel close:', { 
          pipelinePath: activePipeline.id, 
          type: activePipeline.type 
        });
        
        // Clear dropdown cache (Layer 1)
        dropdownCache.clearPipelineCache(activePipeline.id);
        
        // Clear transform values cache (Layer 2) 
        invalidatePipelineTransformCache(activePipeline.id);
      }
      
      setUIConfig(null);
      setValueManagerConfig(null);
      setCurrentValues({});
      setOriginalValues({});
      setDraftValues({});
    }
  }, [nodeId, isDoubleClicked, loading]); // Using stable nodeId from hook instead of selectedNode?.id

  const loadTransformConfiguration = async (transformType: string, nodeData: Record<string, any>) => {
    const nodeId = selectedNode?.id;
    if (!nodeId) return;

    // Set loading flag to prevent duplicate calls
    isLoadingRef.current = true;

    logger.debug('🚀 Starting smart loadTransformConfiguration:', {
      transformType,
      nodeId,
      nodeDataKeys: Object.keys(nodeData),
    });

    setLoading(true);
    try {
      // Get or initialize transform state
      let transformState = getTransformState(nodeId);
      if (!transformState) {
        // Initialize state - determine if transform is new or existing
        const isNewTransform = !nodeData._state && Object.keys(nodeData).length <= 5; // Basic node properties only
        initializeTransformState(nodeId, isNewTransform);
        transformState = getTransformState(nodeId)!;
        logger.debug('🔧 Initialized transform state:', { isNewTransform, transformState });
      }

              logger.debug('🧠 Transform state analysis:', {
        isNew: transformState.isNew,
        isModified: transformState.isModified,
        cacheValid: transformState.cacheValid,
        source: transformState.source,
        lastFetched: transformState.lastFetched,
        hasCachedValues: !!nodeData._cachedValues,
        isWorkflow: isWorkflowAction(),
        nodeType: transformType,
      });

      // Determine if we need to fetch from backend
      const needsFetch = 
        !transformState.cacheValid || 
        !transformState.lastFetched ||
        (transformState.isNew && transformState.source !== 'defaults') ||
        (!transformState.isNew && !transformState.isModified && transformState.source !== 'file');

      logger.debug('🤔 Cache decision analysis:', {
        needsFetch,
        cacheValid: transformState.cacheValid,
        lastFetched: transformState.lastFetched,
        isNew: transformState.isNew,
        source: transformState.source,
        isModified: transformState.isModified,
        isWorkflow: isWorkflowAction(),
        nodeType: transformType,
      });

      let backendValues: any = null;
      let valuesSource: string = 'cache';
      let fetchedUIConfig: PropertyUIConfig | null = null;

      if (needsFetch) {
        logger.debug('📡 Fetching from backend...');
        
        // Always fetch UI configuration (lightweight)
        const uiPromise = isWorkflowAction() 
          ? workflowsService.getActionUIConfiguration(transformType)
          : transformsService.getTransformUIConfiguration(transformType);
        
        // Fetch values based on transform state
        let valuesPromise: Promise<any>;
        if (transformState.isNew) {
          logger.debug('🆕 New transform/action - using empty values (UI defaults will be applied)');
          valuesPromise = Promise.resolve({ values: {} });
          valuesSource = 'ui-defaults';
        } else if (transformState.isModified) {
          logger.debug('⚠️ Using cached values for modified transform (not re-fetching)');
          valuesPromise = Promise.resolve({ values: nodeData._cachedValues || {} });
          valuesSource = 'cache (modified)';
        } else {
          // Existing unmodified transform - determine if we can get from file
          const pipelineId = activePipeline?.id;
          const isPipelineFromFile = pipelineId && (pipelineId.includes('/') || pipelineId.includes('\\') || pipelineId.includes('.'));
          
          if (isPipelineFromFile) {
            logger.debug('📁 Fetching current values from pipeline/workflow file');
            valuesPromise = isWorkflowAction()
              ? workflowsService.getActionValues(pipelineId, nodeId)
              : transformsService.getTransformValues(pipelineId, nodeId, transformType);
            valuesSource = 'file';
          } else {
            logger.debug('🆕 Pipeline/workflow not from file, using empty values (UI defaults will be applied)');
            valuesPromise = Promise.resolve({ values: {} });
            valuesSource = 'ui-defaults';
          }
        }

        const [transformUI, values] = await Promise.all([uiPromise, valuesPromise]);
        backendValues = values;

        // Cache the fetched values
        if (backendValues?.values) {
          cacheTransformValues(nodeId, backendValues.values, valuesSource as any);
          logger.debug('💾 Cached values:', { source: valuesSource, valuesCount: Object.keys(backendValues.values).length });
        }

        // Convert UI config for later use
        if (transformUI) {
          fetchedUIConfig = normalizeUIConfig(transformUI);
        }
      } else {
        logger.debug('💨 Using cached values (no backend fetch needed)');
        backendValues = { values: nodeData._cachedValues || {} };
        valuesSource = 'cache';
        
        // Always fetch UI config for cached values (it's lightweight)
        const transformUI = await (isWorkflowAction() 
          ? workflowsService.getActionUIConfiguration(transformType)
          : transformsService.getTransformUIConfiguration(transformType));
        if (transformUI) {
          fetchedUIConfig = normalizeUIConfig(transformUI);
        }
      }

      // Only proceed if we have UI config
      if (!fetchedUIConfig) {
        const error = new Error('No UI configuration available from backend');
        logger.error('❌ No UI configuration available');
        telemetryService.trackError(error, 'properties-panel-config', {
          transformType,
          nodeId,
          context: 'missing-ui-config'
        });
        // Clear both configs to ensure consistent state
        setUIConfig(null);
        setValueManagerConfig(null);
        return;
      }

      // Merge values in order of precedence
      let initialValues: Record<string, any> = {};
      
      // Start with backend/cached values
      if (backendValues?.values) {
        initialValues = { ...backendValues.values };
      }
      
      // Extract default values from UI components as fallback
      fetchedUIConfig.pages.forEach(page => {
        page.components.forEach(component => {
          if (component.defaultValue !== undefined && !(component.id in initialValues)) {
            initialValues[component.id] = component.defaultValue;
          }
        });
      });
      
      // Override with any current user edits (preserves user changes)
      const filteredNodeData = Object.fromEntries(
        Object.entries(nodeData).filter(([key]) => !key.startsWith('_'))
      );
      initialValues = { ...initialValues, ...filteredNodeData };

      logger.debug('📊 Smart configuration loaded:', {
        transformType,
        transformState: {
          isNew: transformState.isNew,
          isModified: transformState.isModified,
          source: transformState.source,
        },
        valuesSource,
        needsFetch,
        backendValuesCount: Object.keys(backendValues?.values || {}).length,
        finalValuesCount: Object.keys(initialValues).length,
        uiPagesCount: fetchedUIConfig.pages.length,
      });
      
      // Set UI configuration in React state
      setUIConfig(fetchedUIConfig);
      
      // Store values for state management
      setOriginalValues(initialValues);
      setCurrentValues(initialValues);
      setDraftValues(initialValues);
      
      // Set value manager configuration with smart change tracking
      setValueManagerConfig({
        initialValues,
        pages: fetchedUIConfig.pages,
        onChange: (newValues) => {
          setDraftValues(newValues);
          setCurrentValues(newValues);
          
          // Only mark as modified for change tracking, but don't apply to node data yet
          // This prevents the infinite loop by not triggering store updates during editing
          const transformState = getTransformState(nodeId);
          if (transformState) {
            const originalValues = transformState.originalValues || {};
            const hasChanges = JSON.stringify(newValues) !== JSON.stringify(originalValues);
            updateTransformState(nodeId, {
              isModified: hasChanges,
              source: 'user',
            });
            
            // Mark tab as modified when transform properties change
            if (hasChanges && activePipeline?.id) {
              import('../../store/tabManagerStore').then(({ default: useTabManagerStore }) => {
                const { updateTabModified } = useTabManagerStore.getState();
                updateTabModified(activePipeline.id, true);
              });
            }
          }
        }
      });
      
    } catch (error) {
      logger.error('❌ Failed to load transform configuration:', error);
      telemetryService.trackError(error as Error, 'transform-configuration-load', {
        transformType,
        nodeId,
        context: 'properties-panel-load-failure',
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      
      // Clear both configs to ensure consistent state
      setUIConfig(null);
      setValueManagerConfig(null);
      logger.warn('⚠️ Using fallback: Properties panel will not show due to API error');
    } finally {
      setLoading(false);
      isLoadingRef.current = false; // Reset loading flag
      logger.debug('🏁 Smart loadTransformConfiguration completed');
    }
  };

  const handleSave = useCallback(async (values: Record<string, any>): Promise<void> => {
    if (selectedNode && uiConfig) {
      try {
        logger.debug('💾 Applying transform configuration changes:', {
          nodeId: selectedNode.id,
          transformType: uiConfig.transformType,
          values: draftValues
        });

        // Check if the name has changed (structural change)
        const nameChanged = originalValues.name !== draftValues.name;
        
        if (nameChanged) {
          logger.debug('📝 Transform name changed - marking as structural change:', {
            oldName: originalValues.name,
            newName: draftValues.name,
            hasExistingOriginalName: !!selectedNode.data?._state?.originalValues?.name
          });
          
          // Store the original name in node data's _state for backend rename detection
          // IMPORTANT: Only set originalName if it doesn't already exist!
          // This preserves the FIRST name across multiple renames
          const existingOriginalName = selectedNode.data?._state?.originalValues?.name;
          const originalNameToStore = existingOriginalName || originalValues.name;
          
          const updatedDraftValues = {
            ...draftValues,
            _state: {
              ...selectedNode.data?._state,
              originalValues: {
                ...(selectedNode.data?._state?.originalValues || {}),
                name: originalNameToStore // Preserve the ORIGINAL name across multiple renames
              }
            }
          };
          
          logger.debug('📝 Original name tracking:', {
            currentName: draftValues.name,
            previousName: originalValues.name,
            storedOriginalName: originalNameToStore,
            wasAlreadySet: !!existingOriginalName
          });
          
          // Update node data with the new values INCLUDING the preserved original name
          updateNodeData(selectedNode.id, updatedDraftValues);
        } else {
          // No name change, just update normally
          updateNodeData(selectedNode.id, draftValues);
        }
        
        // Mark the transform as modified (but don't reset the state like a real save)
        // The actual save will happen when the main pipeline save button is pressed
        updateTransformState(selectedNode.id, {
          isModified: true,        // Mark as modified for change tracking
          source: 'user'           // User has made changes
        });
        
        // If name changed, mark as structural change
        if (nameChanged && activePipeline) {
          markStructuralChange(activePipeline.id);
        }
        
        // Cache the current values for future reference
        cacheTransformValues(selectedNode.id, draftValues, 'user');
        
        // Update original values to reflect the current state
        // But preserve the original name if it was changed
        if (nameChanged) {
          // Keep the original name in our local state for tracking
          setOriginalValues({
            ...draftValues,
            _originalName: originalValues.name
          });
        } else {
          setOriginalValues(draftValues);
        }
        
        // Clear dropdown cache when closing the properties panel
        if (activePipeline?.id) {
          logger.debug('🧹 Clearing dropdown cache on panel close:', { pipelinePath: activePipeline.id });
          dropdownCache.clearPipelineCache(activePipeline.id);
        }
        
        // Close the panel
        setSelectedNodeId(null, false);
        setUIConfig(null);
        setValueManagerConfig(null);
        setOriginalValues({});
        setDraftValues({});
        
        logger.debug('✅ Configuration changes applied successfully');
      } catch (error) {
        logger.error('❌ Failed to apply configuration changes:', error);
        telemetryService.trackError(error as Error, 'configuration-apply', {
          nodeId: selectedNode?.id,
          transformType: uiConfig?.transformType,
          context: 'properties-panel-apply-failure',
          errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
          errorMessage: error instanceof Error ? error.message : String(error)
        });
        // TODO: Show error notification to user
      }
    }
  }, [selectedNode, uiConfig, draftValues, originalValues, updateNodeData, setSelectedNodeId, updateTransformState, cacheTransformValues, activePipeline, markStructuralChange]);

  const handleCancel = useCallback(() => {
    // Revert any changes by restoring original values to the node
    if (selectedNode && originalValues) {
      updateNodeData(selectedNode.id, originalValues);
    }
    
    // Clear dropdown cache when closing the properties panel
    if (activePipeline?.id) {
      logger.debug('🧹 Clearing dropdown cache on panel cancel:', { pipelinePath: activePipeline.id });
      dropdownCache.clearPipelineCache(activePipeline.id);
    }
    
    // Close the panel without saving
    setSelectedNodeId(null, false);
    setUIConfig(null);
    setValueManagerConfig(null);
    setCurrentValues({});
    setOriginalValues({});
    setDraftValues({});
  }, [selectedNode, originalValues, updateNodeData, setSelectedNodeId, activePipeline]);

  const handleClose = useCallback(() => {
    // Close without changes - same as cancel
    handleCancel();
  }, [handleCancel]);

  // Handle resize functionality (for floating and docked modes)
  const handleResizeMouseDown = useCallback((e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = propertiesPanelSize.width;
    const startHeight = propertiesPanelSize.height;
    const startPos = position;

    const handleMouseMove = (e: MouseEvent) => {
      const minWidth = 400;
      const minHeight = 300;
      const maxWidth = window.innerWidth * 0.9;
      const maxHeight = window.innerHeight * 0.9;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newPos = { ...startPos };

      // Calculate new dimensions based on resize direction
      switch (direction) {
        case 'left':
          newWidth = startWidth - (e.clientX - startX);
          if (newWidth >= minWidth && newWidth <= maxWidth) {
            const widthDiff = newWidth - startWidth;
            newPos.x = startPos.x - widthDiff;
          }
          break;
        case 'right':
          newWidth = startWidth + (e.clientX - startX);
          break;
        case 'top':
          newHeight = startHeight - (e.clientY - startY);
          if (newHeight >= minHeight && newHeight <= maxHeight) {
            const heightDiff = newHeight - startHeight;
            newPos.y = startPos.y - heightDiff;
          }
          break;
        case 'bottom':
          newHeight = startHeight + (e.clientY - startY);
          break;
        case 'top-left':
          newWidth = startWidth - (e.clientX - startX);
          newHeight = startHeight - (e.clientY - startY);
          if (newWidth >= minWidth && newWidth <= maxWidth) {
            const widthDiff = newWidth - startWidth;
            newPos.x = startPos.x - widthDiff;
          }
          if (newHeight >= minHeight && newHeight <= maxHeight) {
            const heightDiff = newHeight - startHeight;
            newPos.y = startPos.y - heightDiff;
          }
          break;
        case 'top-right':
          newWidth = startWidth + (e.clientX - startX);
          newHeight = startHeight - (e.clientY - startY);
          if (newHeight >= minHeight && newHeight <= maxHeight) {
            const heightDiff = newHeight - startHeight;
            newPos.y = startPos.y - heightDiff;
          }
          break;
        case 'bottom-left':
          newWidth = startWidth - (e.clientX - startX);
          newHeight = startHeight + (e.clientY - startY);
          if (newWidth >= minWidth && newWidth <= maxWidth) {
            const widthDiff = newWidth - startWidth;
            newPos.x = startPos.x - widthDiff;
          }
          break;
        case 'bottom-right':
          newWidth = startWidth + (e.clientX - startX);
          newHeight = startHeight + (e.clientY - startY);
          break;
      }

      // Apply constraints
      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

      // Keep panel within viewport bounds
      // Calculate bounds to allow panel edges to touch viewport edges
      const minX = newWidth / 2 - window.innerWidth / 2;
      const minY = newHeight / 2 - window.innerHeight / 2;
      const maxX = window.innerWidth / 2 - newWidth / 2;
      const maxY = window.innerHeight / 2 - newHeight / 2;
      newPos.x = Math.max(minX, Math.min(maxX, newPos.x));
      newPos.y = Math.max(minY, Math.min(maxY, newPos.y));

      setPropertiesPanelSize({ width: newWidth, height: newHeight });
      setPosition(newPos);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [propertiesPanelSize, setPropertiesPanelSize, position, setPosition]);

  // Handle dragging functionality for moving the panel
  const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    // Only allow dragging from the header area, not buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setHoveredDockZone(null);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = position;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = startPos.x + (e.clientX - startX);
      const newY = startPos.y + (e.clientY - startY);
      
      // Keep panel within viewport bounds
      // Calculate bounds to allow panel edges to touch viewport edges
      const minX = propertiesPanelSize.width / 2 - window.innerWidth / 2;
      const minY = propertiesPanelSize.height / 2 - window.innerHeight / 2;
      const maxX = window.innerWidth / 2 - propertiesPanelSize.width / 2;
      const maxY = window.innerHeight / 2 - propertiesPanelSize.height / 2;
      
      setPosition({
        x: Math.max(minX, Math.min(maxX, newX)),
        y: Math.max(minY, Math.min(maxY, newY))
      });

      // Check for dock zone hovering
      const rightZone = e.clientX > window.innerWidth - 60;
      const bottomZone = e.clientY > window.innerHeight - 60;
      
      if (rightZone) {
        setHoveredDockZone('right');
      } else if (bottomZone) {
        setHoveredDockZone('bottom');
      } else {
        setHoveredDockZone(null);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      const rightZone = e.clientX > window.innerWidth - 60;
      const bottomZone = e.clientY > window.innerHeight - 60;

      // Dock if dropped in a dock zone
      if (rightZone) {
        setPropertiesPanelDock('right');
      } else if (bottomZone) {
        setPropertiesPanelDock('bottom');
      }

      setIsDragging(false);
      setHoveredDockZone(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [position, propertiesPanelSize, setPropertiesPanelDock]);

  // Add cursor style when resizing or dragging
  useEffect(() => {
    if (isResizing) {
      let cursor = 'ew-resize';
      switch (resizeDirection) {
        case 'top':
        case 'bottom':
          cursor = 'ns-resize';
          break;
        case 'top-left':
        case 'bottom-right':
          cursor = 'nw-resize';
          break;
        case 'top-right':
        case 'bottom-left':
          cursor = 'ne-resize';
          break;
      }
      document.body.style.cursor = cursor;
    } else if (isDragging) {
      document.body.style.cursor = 'move';
    } else {
      document.body.style.cursor = '';
    }
    
    return () => {
      document.body.style.cursor = '';
    };
  }, [isResizing, isDragging, resizeDirection]);

  // Add keyboard event handling for Tab and Escape to close the properties panel
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keyboard shortcuts when the properties panel is open
      if (!nodeId || !isDoubleClicked) {
        return;
      }

      // Close properties panel on Tab or Escape key
      if (event.key === 'Escape') {
        event.preventDefault(); // Prevent default Tab behavior
        handleClose();
      }
    };

    // Add event listener when panel is open
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [nodeId, isDoubleClicked, handleClose]);

  // Don't render anything if panel is not open
  if (!nodeId || !isDoubleClicked) {
    return null;
  }

  // Render docked panel content only (no backdrop, positioning handled by parent)
  if (propertiesPanelDock !== 'floating') {
    return (
      <div 
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        data-testid="properties-panel"
      >
        {/* Docked panel header */}
        <div
          data-testid="properties-panel-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: token.colorBgLayout,
            minHeight: '36px',
          }}
        >
          <h3 data-testid="properties-panel-title" style={{ margin: 0, fontSize: '13px', fontWeight: 600 }}>
            {uiConfig ? `Configure: ${uiConfig.transformName}` : `Configure: ${selectedNode?.data?.name || nodeId}`}
          </h3>
          <div style={{ display: 'flex', gap: '4px' }}>
            <Button
              type="text"
              size="small"
              icon={<PushpinOutlined />}
              onClick={() => setPropertiesPanelDock('floating')}
              title="Undock to floating"
            />
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={handleClose}
              data-testid="properties-panel-close-button"
            />
          </div>
        </div>

        {/* Docked panel content */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {loading && (
            <div style={{ padding: 24, textAlign: 'center', overflow: 'auto' }}>
              Loading configuration...
            </div>
          )}
          
          {!loading && !uiConfig && nodeId && (
            <div style={{ padding: 24, textAlign: 'center', color: '#999', overflow: 'auto' }}>
              No configuration available for this transform type: {selectedNode?.type || nodeId}
            </div>
          )}
          
          {!loading && uiConfig && valueManagerConfig && (
            <PropertyValueProvider config={valueManagerConfig}>
              <PropertyWizard
                config={uiConfig}
                onSave={handleSave}
                onCancel={handleClose}
              />
            </PropertyValueProvider>
          )}
        </div>

        {/* Top resize handle for docked mode */}
        {propertiesPanelDock === 'bottom' && (
          <div
            data-testid="properties-panel-resize-handle-top"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: isResizing ? '#1890ff' : 'transparent',
              cursor: 'ns-resize',
              zIndex: 1002,
              borderTop: isResizing ? '2px solid #1890ff' : '2px solid transparent',
              transition: isResizing ? 'none' : 'all 0.2s ease',
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'top')}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              if (!isResizing) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {/* Backdrop overlay (floating mode only) */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          pointerEvents: 'auto',
        }}
        onClick={handleClose}
      />

      {/* Dock zones */}
      {isDragging && (
        <>
          {/* Right dock zone */}
          <div
            style={{
              position: 'fixed',
              right: 0,
              top: 0,
              width: '60px',
              height: '100vh',
              backgroundColor: hoveredDockZone === 'right' ? 'rgba(24, 144, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
              zIndex: 999,
              border: hoveredDockZone === 'right' ? '2px solid #1890ff' : '2px solid transparent',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: hoveredDockZone === 'right' ? '#1890ff' : '#666',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
            onMouseEnter={() => setHoveredDockZone('right')}
            onMouseLeave={() => setHoveredDockZone(null)}
            onMouseUp={() => {
              setPropertiesPanelDock('right');
              setIsDragging(false);
              setHoveredDockZone(null);
            }}
          >
            DOCK RIGHT
          </div>

          {/* Bottom dock zone */}
          <div
            style={{
              position: 'fixed',
              left: 0,
              bottom: 0,
              width: '100vw',
              height: '60px',
              backgroundColor: hoveredDockZone === 'bottom' ? 'rgba(24, 144, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
              zIndex: 999,
              border: hoveredDockZone === 'bottom' ? '2px solid #1890ff' : '2px solid transparent',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: hoveredDockZone === 'bottom' ? '#1890ff' : '#666',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
            onMouseEnter={() => setHoveredDockZone('bottom')}
            onMouseLeave={() => setHoveredDockZone(null)}
            onMouseUp={() => {
              setPropertiesPanelDock('bottom');
              setIsDragging(false);
              setHoveredDockZone(null);
            }}
          >
            DOCK BOTTOM
          </div>
        </>
      )}

      {/* Floating properties panel */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          width: propertiesPanelSize.width,
          height: propertiesPanelSize.height,
          maxWidth: '90vw',
          maxHeight: '85vh',
          backgroundColor: token.colorBgContainer,
          borderRadius: '8px',
          boxShadow: isDragging || isResizing ? '0 12px 32px rgba(0, 0, 0, 0.2)' : '0 8px 24px rgba(0, 0, 0, 0.12)',
          zIndex: 1001,
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          transition: isDragging || isResizing ? 'none' : 'box-shadow 0.2s ease',
        }}
        data-testid="properties-panel"
      >
        {/* Header */}
        <div
          data-testid="properties-panel-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: token.colorBgLayout,
            borderRadius: '8px 8px 0 0',
            cursor: isDragging ? 'move' : 'grab',
            userSelect: 'none',
          }}
          onMouseDown={handleHeaderMouseDown}
        >
          <h3 data-testid="properties-panel-title" style={{ margin: 0, fontSize: '14px', fontWeight: 600, pointerEvents: 'none' }}>
            {uiConfig ? `Configure: ${uiConfig.transformName}` : `Configure: ${selectedNode?.data?.name || nodeId}`}
          </h3>
          <div style={{ display: 'flex', gap: '4px' }}>
            <Button
              type="text"
              icon={<PushpinOutlined />}
              onClick={() => setPropertiesPanelDock('right')}
              style={{ padding: '4px' }}
              title="Dock to right"
              onMouseDown={(e) => e.stopPropagation()}
            />
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={handleClose}
              style={{ padding: '4px' }}
              onMouseDown={(e) => e.stopPropagation()}
              data-testid="properties-panel-close-button"
            />
          </div>
        </div>

        {/* Resize handles for floating mode */}
        {propertiesPanelDock === 'floating' && (
          <>
            {/* Left resize handle */}
            <div
              data-testid="properties-panel-resize-handle-left"
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '4px',
                backgroundColor: isResizing && resizeDirection === 'left' ? '#1890ff' : 'transparent',
                cursor: 'ew-resize',
                zIndex: 1002,
                borderLeft: isResizing && resizeDirection === 'left' ? '2px solid #1890ff' : '2px solid transparent',
                transition: isResizing ? 'none' : 'all 0.2s ease',
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
              onMouseEnter={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            />

            {/* Right resize handle */}
            <div
              data-testid="properties-panel-resize-handle-right"
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '4px',
                backgroundColor: isResizing && resizeDirection === 'right' ? '#1890ff' : 'transparent',
                cursor: 'ew-resize',
                zIndex: 1002,
                borderRight: isResizing && resizeDirection === 'right' ? '2px solid #1890ff' : '2px solid transparent',
                transition: isResizing ? 'none' : 'all 0.2s ease',
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
              onMouseEnter={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            />

            {/* Top resize handle */}
            <div
              data-testid="properties-panel-resize-handle-top"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: isResizing && resizeDirection === 'top' ? '#1890ff' : 'transparent',
                cursor: 'ns-resize',
                zIndex: 1002,
                borderTop: isResizing && resizeDirection === 'top' ? '2px solid #1890ff' : '2px solid transparent',
                transition: isResizing ? 'none' : 'all 0.2s ease',
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'top')}
              onMouseEnter={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            />

            {/* Bottom resize handle */}
            <div
              data-testid="properties-panel-resize-handle-bottom"
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: isResizing && resizeDirection === 'bottom' ? '#1890ff' : 'transparent',
                cursor: 'ns-resize',
                zIndex: 1002,
                borderBottom: isResizing && resizeDirection === 'bottom' ? '2px solid #1890ff' : '2px solid transparent',
                transition: isResizing ? 'none' : 'all 0.2s ease',
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
              onMouseEnter={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            />

            {/* Corner resize handles */}
            {/* Top-left corner */}
            <div
              data-testid="properties-panel-resize-handle-top-left"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '8px',
                height: '8px',
                cursor: 'nw-resize',
                zIndex: 1003,
                backgroundColor: isResizing && resizeDirection === 'top-left' ? '#1890ff' : 'transparent',
                borderTop: isResizing && resizeDirection === 'top-left' ? '2px solid #1890ff' : '2px solid transparent',
                borderLeft: isResizing && resizeDirection === 'top-left' ? '2px solid #1890ff' : '2px solid transparent',
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'top-left')}
            />

            {/* Top-right corner */}
            <div
              data-testid="properties-panel-resize-handle-top-right"
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '8px',
                height: '8px',
                cursor: 'ne-resize',
                zIndex: 1003,
                backgroundColor: isResizing && resizeDirection === 'top-right' ? '#1890ff' : 'transparent',
                borderTop: isResizing && resizeDirection === 'top-right' ? '2px solid #1890ff' : '2px solid transparent',
                borderRight: isResizing && resizeDirection === 'top-right' ? '2px solid #1890ff' : '2px solid transparent',
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'top-right')}
            />

            {/* Bottom-left corner */}
            <div
              data-testid="properties-panel-resize-handle-bottom-left"
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '8px',
                height: '8px',
                cursor: 'ne-resize',
                zIndex: 1003,
                backgroundColor: isResizing && resizeDirection === 'bottom-left' ? '#1890ff' : 'transparent',
                borderBottom: isResizing && resizeDirection === 'bottom-left' ? '2px solid #1890ff' : '2px solid transparent',
                borderLeft: isResizing && resizeDirection === 'bottom-left' ? '2px solid #1890ff' : '2px solid transparent',
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-left')}
            />

            {/* Bottom-right corner */}
            <div
              data-testid="properties-panel-resize-handle-bottom-right"
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '8px',
                height: '8px',
                cursor: 'nw-resize',
                zIndex: 1003,
                backgroundColor: isResizing && resizeDirection === 'bottom-right' ? '#1890ff' : 'transparent',
                borderBottom: isResizing && resizeDirection === 'bottom-right' ? '2px solid #1890ff' : '2px solid transparent',
                borderRight: isResizing && resizeDirection === 'bottom-right' ? '2px solid #1890ff' : '2px solid transparent',
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-right')}
            />
          </>
        )}

        {/* Content */}
        <div
          style={{
            height: `calc(${propertiesPanelSize.height}px - 48px)`, // Account for smaller header height
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {loading && (
            <div style={{ padding: 24, textAlign: 'center', overflow: 'auto', flex: 1 }}>
              Loading configuration...
            </div>
          )}
          
          {!loading && !uiConfig && nodeId && (
            <div style={{ padding: 24, textAlign: 'center', color: '#999', overflow: 'auto', flex: 1 }}>
              No configuration available for this transform type: {selectedNode?.type || nodeId}
            </div>
          )}
          
          {!loading && uiConfig && valueManagerConfig && (
            <PropertyValueProvider config={valueManagerConfig}>
              <PropertyWizard
                config={uiConfig}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            </PropertyValueProvider>
          )}
        </div>
      </div>
    </>
  );
});

PropertiesPanel.displayName = 'PropertiesPanel';

export default PropertiesPanel;
